/**
 * @file ConfigService.ts
 * @role Loads and validates game config at boot. v1 reads the bundled JSON; the seam
 *       (raw injected via constructor) lets a remote/fetched config drop in later and
 *       lets tests feed malformed input. Validation is the config trust boundary.
 * @owns The validated GameConfig and typed access to it.
 * @reads Bundled game.config.json (default) or injected raw config.
 * @neverDoes Mutate config. Decide outcomes. Touch Pixi/HTTP.
 * @invariants 10 (validate before use), 7 (money fields are integers — schema-enforced).
 * @see docs/CONFIG_GUIDE.md, src/core/config/ConfigSchema.ts
 */
import gameConfigRaw from '@config/game.config.json';
import { GameError } from '@core/errors/GameError';
import { GameConfigSchema, type GameConfig } from './ConfigSchema';

export class ConfigService {
  private gameConfig: GameConfig | undefined;

  /** @param rawGameConfig defaults to the bundled config; injectable for tests. */
  constructor(private readonly rawGameConfig: unknown = gameConfigRaw) {}

  /** Validate the raw config (the trust boundary). Throws CONFIG_LOAD_FAILED if invalid. */
  load(): void {
    const result = GameConfigSchema.safeParse(this.rawGameConfig);
    if (!result.success) {
      throw new GameError('CONFIG_LOAD_FAILED', `Invalid game config: ${result.error.message}`, {
        recoverable: false,
        cause: result.error,
      });
    }
    this.gameConfig = result.data;
  }

  get game(): GameConfig {
    if (!this.gameConfig) {
      throw new GameError('CONFIG_LOAD_FAILED', 'Config accessed before load()');
    }
    return this.gameConfig;
  }
}
